import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {useDispatch, useSelector} from "react-redux";

import css from './PromoCodesUpdatePromoCodeModal.module.css';
import {packsActions, periodsActions, promoCodesActions} from "../../../../store/slices/settings-slices";
import {promoCodesService} from "../../../../services/settings-services";


const PromoCodesUpdatePromoCodeModal = ({setIsOpenUpdate, promoCodeObj}) => {
    const dispatch = useDispatch();
    const {t, i18n} = useTranslation();
    const [isShow, setIsShow] = useState(() => promoCodeObj?.promoMode === 'TABLE');
    const {packs} = useSelector(store => store.packs);
    const {periods} = useSelector(store => store.periods);
    const [matrix, setMatrix] = useState({});
    const [existingRules, setExistingRules] = useState([]);
    const [deletingId, setDeletingId] = useState(null);

    const packHeaders = useMemo(() => {
        const lang = i18n.language;
        return (packs || []).map(p => ({
            id: p.id,
            name: (p.name && (p.name[lang] || p.name["en"])) || String(p.id)
        }));
    }, [packs, i18n.language]);

    const {id} = promoCodeObj;

    useEffect(() => {
        dispatch(packsActions.getAllPacks());
        dispatch(periodsActions.getAllPeriods());
    }, [dispatch]);

    useEffect(() => {
        setExistingRules(promoCodeObj?.promoTableRules || []);
    }, [promoCodeObj]);

    useEffect(() => {
        if (!promoCodeObj?.promoTableRules?.length) {
            setMatrix({});
            return;
        }

        const initialMatrix = promoCodeObj.promoTableRules.reduce((acc, rule) => {
            if (!rule.packId || !rule.periodId) {
                return acc;
            }

            const amountValue = rule.discountType === 'Percentage'
                ? (rule.percent ?? '')
                : (rule.amount ?? '');

            return {
                ...acc,
                [rule.packId]: {
                    ...(acc[rule.packId] || {}),
                    [rule.periodId]: {
                        discountType: rule.discountType || 'Percentage',
                        amount: amountValue === null ? '' : amountValue
                    }
                }
            };
        }, {});

        setMatrix(initialMatrix);
    }, [promoCodeObj]);

    const packIdToName = useMemo(() => {
        const lang = i18n.language;
        const map = {};
        (packs || []).forEach(p => {
            map[p.id] = (p.name && (p.name[lang] || p.name["en"])) || String(p.id);
        });
        return map;
    }, [packs, i18n.language]);

    const periodIdToDays = useMemo(() => {
        const map = {};
        (periods || []).forEach(per => {
            map[per.id] = per.daysCount;
        });
        return map;
    }, [periods]);

    const handleCellChange = (packId, periodId, field, value) => {
        setMatrix(prev => {
            const byPack = prev[packId] || {};
            const cell = byPack[periodId] || {discountType: 'Percentage', amount: ''};
            const nextValue = field === 'amount'
                ? (value === '' ? '' : Number(value))
                : value;

            return {
                ...prev,
                [packId]: {
                    ...byPack,
                    [periodId]: {
                        ...cell,
                        [field]: nextValue
                    }
                }
            };
        });
    };

    const handleDeleteRule = async (ruleId, packId, periodId) => {
        if (!ruleId) {
            toast.error('Cannot delete: missing rule id');
            return;
        }
        const confirmed = window.confirm(t('Are-you-sure'));
        if (!confirmed) return;
        try {
            setDeletingId(ruleId);
            await promoCodesService.deletePromoTableRuleById(ruleId);
            setExistingRules(prev => prev.filter(r => r.id !== ruleId));
            setMatrix(prev => {
                const next = {...prev};
                const byPack = next[packId];
                if (!byPack) return next;
                const {[periodId]: _, ...restPeriods} = byPack;
                if (Object.keys(restPeriods).length === 0) {
                    const { [packId]: __, ...restPacks } = next;
                    return restPacks;
                }
                next[packId] = restPeriods;
                return next;
            });
            dispatch(promoCodesActions.changeTrigger());
            toast.success(t('Deleted-successfully'));
        } catch (e) {
            console.error('Failed to delete rule', e);
            toast.error(t('Error'));
        } finally {
            setDeletingId(null);
        }
    };

    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm({
        defaultValues: {
            promoCode: promoCodeObj.promoCode || "",
            staffDescription: promoCodeObj.staffDescription || "",
            validFrom: promoCodeObj.validFrom?.split('T')[0] || "",
            validTo: promoCodeObj.validUntil?.split('T')[0] || "",
            descriptionEn: promoCodeObj.description?.en || "",
            descriptionRu: promoCodeObj.description?.ru || "",
            descriptionKa: promoCodeObj.description?.ge || "",
            appendDiscount: promoCodeObj.appendDiscount || false,
            firstOrder: promoCodeObj.firstOrderOnly || false,
            oneTime: promoCodeObj.oneTimeUse || false,
            discountType: promoCodeObj.discountType || "Percentage",
            amount: promoCodeObj.amount || 0,
        },
    });

    const submitHandler = async (data) => {
        try {
            const fromDate = new Date(data.validFrom);
            const toDate = new Date(data.validTo);

            if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
                toast.error('Please provide valid dates');
                return;
            }

            if (fromDate > toDate) {
                toast.error('"Valid from" must be before or equal to "Valid to"');
                return;
            }

            const formattedData = {
                promoCode: data.promoCode,
                description: {
                    en: data.descriptionEn || "",
                    ru: data.descriptionRu || "",
                    ge: data.descriptionKa || "",
                },
                staffDescription: data.staffDescription || "",
                discountType: data.discountType || "Amount",
                validFrom: fromDate.toISOString(),
                validUntil: toDate.toISOString(),
                appendDiscount: !!data.appendDiscount,
                firstOrderOnly: !!data.firstOrder,
                oneTimeUse: !!data.oneTime,
            };

            if (isShow) {
                formattedData.promoMode = 'TABLE';
                formattedData.amount = 0;

                const rules = [];

                Object.entries(matrix || {}).forEach(([packId, periodsMap]) => {
                    Object.entries(periodsMap || {}).forEach(([periodId, cell]) => {
                        if (!cell?.discountType) {
                            return;
                        }

                        const rule = {
                            packId,
                            periodId,
                            discountType: cell.discountType,
                        };

                        if (cell.discountType === 'Percentage') {
                            const percentValue = cell.amount === '' ? '' : Number(cell.amount);
                            if (percentValue !== '' && !Number.isNaN(percentValue) && percentValue > 0) {
                                rule.percent = percentValue;
                                rules.push(rule);
                            }
                        } else if (cell.discountType === 'Amount' || cell.discountType === 'FreeDays' || cell.discountType === 'AdditionalDays') {
                            const amountValue = cell.amount === '' ? '' : Number(cell.amount);
                            if (amountValue !== '' && !Number.isNaN(amountValue) && amountValue > 0) {
                                rule.amount = amountValue;
                                rules.push(rule);
                            }
                        } else if (cell.discountType === 'FreeDelivery') {
                            rules.push(rule);
                        }
                    });
                });

                formattedData.promoTableRules = rules;
            } else {
                formattedData.promoMode = 'SIMPLE';
                formattedData.amount = Number(data.amount) || 0;
            }

            await promoCodesService.updatePromoCodeById(id, formattedData);
            dispatch(promoCodesActions.changeTrigger());
            toast.success('good');
            setIsOpenUpdate(false);
        } catch (error) {
            console.error("Error updating promo code:", error);
            toast.error('Error updating promo code');
        }
    };


    return (
        <section className={css.modalOverlay}>
            <div className={css.modal}>
                <button className={css.closeButton} onClick={() => setIsOpenUpdate(false)}>
                    &times;
                </button>
                <h2 className={css.title}>{t('modal-promo-codes-update-promo-code')}</h2>

                <form onSubmit={handleSubmit(submitHandler)}>
                    <div className={css.row}>
                        <div className={css.inputGroup}>
                            <label htmlFor="promoCode">{t('Promo-code')}</label>
                            <input
                                type="text"
                                id="promoCode"
                                {...register("promoCode", {required: "Promo Code is required"})}
                                className={errors.promoCode ? css.inputError : ""}
                            />
                            {errors.promoCode && (
                                <p className={css.error}>{errors.promoCode.message}</p>
                            )}
                        </div>
                        <div className={css.inputGroup}>
                            <label htmlFor="staffDescription">{t('Staff-description')}</label>
                            <input
                                type="text"
                                id="staffDescription"
                                {...register("staffDescription")}
                            />
                        </div>
                    </div>

                    <div className={css.row}>
                        <div className={css.inputGroup}>
                            <label htmlFor="validFrom">{t('Valid-from')}</label>
                            <input
                                type="date"
                                id="validFrom"
                                {...register("validFrom", {required: "Valid From is required"})}
                            />
                        </div>
                        <div className={css.inputGroup}>
                            <label htmlFor="validTo">{t('Valid-to')}</label>
                            <input
                                type="date"
                                id="validTo"
                                {...register("validTo", {required: "Valid To is required"})}
                            />
                        </div>
                    </div>

                    <div className={css.row}>
                        <div className={css.inputGroup}>
                            <label htmlFor="descriptionEn">{t('Client-description')} (English)</label>
                            <input type="text" id="descriptionEn" {...register("descriptionEn")} />
                        </div>
                        <div className={css.inputGroup}>
                            <label htmlFor="descriptionRu">{t('Client-description')} (Русский)</label>
                            <input type="text" id="descriptionRu" {...register("descriptionRu")} />
                        </div>
                        <div className={css.inputGroup}>
                            <label htmlFor="descriptionKa">{t('Client-description')} (ქართული)</label>
                            <input type="text" id="descriptionKa" {...register("descriptionKa")} />
                        </div>
                    </div>

                    <div className={css.checkboxGroup}>
                        <label>
                            <input type="checkbox" {...register("appendDiscount")} />
                            <span className={css.nameCheckbox}>{t('Append-discount')}</span>
                        </label>
                        <label>
                            <input type="checkbox" {...register("firstOrder")} />
                            <span className={css.nameCheckbox}>{t('First-order')}</span>
                        </label>
                        <label>
                            <input type="checkbox" {...register("oneTime")} />
                            <span className={css.nameCheckbox}>{t('One-time')}</span>
                        </label>
                    </div>

                    <div className={css.row}>
                        <div className={css.inputGroup}>
                            <label htmlFor="discountType">{t('Discount-type')}</label>
                            <select id="discountType"
                                    {...register("discountType")}
                                    className={css.selectDiscount}
                            >
                                <option value="Percentage">{t('Percentage')}</option>
                                <option value="Amount">{t('Amount')}</option>
                                <option value="FreeDelivery">{t('Free-delivery')}</option>
                                <option value="FreeDays">{t('Free-days')}</option>
                                <option value="AdditionalDays">{t('Additional-days')}</option>
                            </select>
                        </div>

                        <div className={`${css.inputGroup} ${css.block}`}>
                            <label>{t('Type')}</label>
                            <div className={css.buttonGroup}>
                                <button type="button"
                                        className={`${css.btn} ${!isShow ? css.buttonActive : ''}`}
                                        onClick={() => setIsShow(false)}>
                                    {t('Simple')}
                                </button>
                                <button type="button"
                                        className={`${css.btn} ${isShow ? css.buttonActive : ''}`}
                                        onClick={() => setIsShow(true)}>
                                    {t('Table')}
                                </button>
                            </div>
                        </div>

                        <div className={`${css.inputGroup} ${isShow ? css.none : ''}`}>
                            <label htmlFor="amount">{t('Amount')}</label>
                            <input
                                type="number"
                                id="amount"
                                {...register("amount")}
                            />
                        </div>
                    </div>

                    {isShow && (
                        <div className={css.row}>
                            <div className={css.inputGroup} style={{width: '100%', overflowX: 'auto'}}>
                                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                    <thead>
                                    <tr>
                                        <th style={{textAlign: 'left', padding: '8px 4px'}}>{t('Periods')}</th>
                                        {(packs || []).map(pack => {
                                            const headerName = (pack.name && (pack.name[i18n.language] || pack.name["en"])) || String(pack.id);
                                            return (
                                                <th key={pack.id} style={{textAlign: 'left', padding: '8px 4px'}}>{headerName}</th>
                                            );
                                        })}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(periods || []).map(per => (
                                        <tr key={per.id}>
                                            <td style={{padding: '6px 4px'}}>{per.daysCount} {t('Days-count')}</td>
                                        {packHeaders.map(pack => {
                                                const cell = matrix?.[pack.id]?.[per.id] || {discountType: 'Percentage', amount: ''};

                                                return (
                                                    <td key={`${pack.id}-${per.id}`} style={{padding: '6px 4px'}}>
                                                        <div style={{display: 'flex', gap: '6px'}}>
                                                            <select
                                                                style={{width: 100}}
                                                                value={cell.discountType}
                                                                onChange={(e) => handleCellChange(pack.id, per.id, 'discountType', e.target.value)}
                                                            >
                                                                <option value="Percentage">{t('Percentage')}</option>
                                                                <option value="Amount">{t('Amount')}</option>
                                                                <option value="FreeDelivery">{t('Free-delivery')}</option>
                                                                <option value="FreeDays">{t('Free-days')}</option>
                                                                <option value="AdditionalDays">{t('Additional-days')}</option>
                                                            </select>
                                                            <input
                                                                type="number"
                                                                value={cell.amount === '' ? '' : cell.amount}
                                                                onChange={(e) => handleCellChange(pack.id, per.id, 'amount', e.target.value)}
                                                                style={{width: 90}}
                                                                disabled={cell.discountType === 'FreeDelivery'}
                                                            />
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {isShow && existingRules?.length > 0 && (
                        <div className={css.row}>
                            <div className={css.inputGroup} style={{width: '100%', overflowX: 'auto'}}>
                                <table style={{width: '100%', borderCollapse: 'collapse', marginTop: 12}}>
                                    <thead>
                                    <tr>
                                        <th style={{textAlign: 'left', padding: '8px 4px'}}>{t('Periods')}</th>
                                        <th style={{textAlign: 'left', padding: '8px 4px'}}>{t('Pack')}</th>
                                        <th style={{textAlign: 'left', padding: '8px 4px'}}>{t('Discount-type')}</th>
                                        <th style={{textAlign: 'left', padding: '8px 4px'}}>{t('Amount')}</th>
                                        <th style={{textAlign: 'left', padding: '8px 4px'}}>{t('Actions')}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {existingRules.map(rule => {
                                        const packName = packIdToName[rule.packId] || String(rule.packId);
                                        const days = periodIdToDays[rule.periodId];
                                        return (
                                            <tr key={rule.id || `${rule.packId}-${rule.periodId}-${rule.discountType}`}>
                                                <td style={{padding: '6px 4px'}}>{days} {t('Days-count')}</td>
                                                <td style={{padding: '6px 4px'}}>{packName}</td>
                                                <td style={{padding: '6px 4px'}}>{rule.discountType}</td>
                                                <td style={{padding: '6px 4px'}}>{rule.percent ?? rule.amount ?? (rule.discountType === 'FreeDelivery' ? t('Free-delivery') : '')}</td>
                                                <td style={{padding: '6px 4px'}}>
                                                    <button
                                                        type="button"
                                                        disabled={deletingId === rule.id}
                                                        onClick={() => handleDeleteRule(rule.id, rule.packId, rule.periodId)}
                                                        className={css.btn}
                                                    >
                                                        {deletingId === rule.id ? t('Deleting...') : t('Delete')}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <button type="submit" className={css.submitButton}>
                        Submit
                    </button>
                </form>
            </div>
        </section>
    );
};

export {PromoCodesUpdatePromoCodeModal};






